import { useEffect, useMemo, useState } from 'react';
import { Radio } from 'antd';
import { FiDatabase, FiFileText, FiPlus } from 'react-icons/fi';
import {
  bankAccountsMatch,
  getBankOptionValue,
  hasBankAccountAndIfsc,
  parseBankOptionKey,
} from '../../../utils/bankStepPrefill';

function formatBankSummary(bank) {
  if (!bank) return '';
  return [bank.bank_name || 'Bank', bank.account_number || '', bank.ifsc || '']
    .filter(Boolean)
    .join(' · ');
}

function defaultSelectedValue(existingBanks, hasCmr) {
  if (existingBanks.length > 0) return getBankOptionValue(existingBanks[0]);
  if (hasCmr) return 'cmr';
  return 'new';
}

function OptionRow({ value, groupValue, icon: Icon, iconClass, title, subtitle }) {
  const selected = groupValue === value;
  return (
    <label
      className={`flex w-full cursor-pointer gap-4 rounded-xl border px-5 py-4 transition-colors ${
        selected ? 'border-green-600 bg-green-50 shadow-sm' : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <Radio value={value} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex items-center gap-2.5">
          <Icon className={`h-4 w-4 shrink-0 ${iconClass}`} aria-hidden />
          <span className="text-sm font-semibold text-gray-900">{title}</span>
        </div>
        {subtitle ? <p className="text-xs leading-relaxed text-gray-500">{subtitle}</p> : null}
      </div>
    </label>
  );
}

/**
 * Opens when there are multiple saved banks or a CMR/saved conflict.
 * Lets the admin pick which source to use for the bank form.
 */
export default function BankSourceChoiceModal({
  open,
  existingBanks,
  cmrFields,
  hasCmrBank: hasCmrBankProp,
  onUseExisting,
  onUseCmr,
  onUseNew,
}) {
  const hasCmrBank = hasCmrBankProp ?? hasBankAccountAndIfsc(cmrFields);

  const [selectedValue, setSelectedValue] = useState(() =>
    defaultSelectedValue(existingBanks, hasCmrBank)
  );

  useEffect(() => {
    if (!open) return;
    setSelectedValue(defaultSelectedValue(existingBanks, hasCmrBank));
  }, [open, existingBanks, hasCmrBank]);

  const selectedOptionKey = parseBankOptionKey(selectedValue);
  const selectedBank = useMemo(
    () => existingBanks.find((b) => String(b.optionKey) === String(selectedOptionKey)),
    [existingBanks, selectedOptionKey]
  );

  const isSavedSelected = Boolean(selectedOptionKey && selectedBank);
  const isCmrSelected = selectedValue === 'cmr';

  const cmrMatchesSaved = useMemo(() => {
    if (!hasCmrBank || existingBanks.length !== 1) return false;
    return bankAccountsMatch(existingBanks[0].account_number, cmrFields?.account_number);
  }, [hasCmrBank, existingBanks, cmrFields?.account_number]);

  const handleContinue = () => {
    if (isSavedSelected && selectedBank) onUseExisting(selectedBank);
    else if (isCmrSelected) onUseCmr();
    else onUseNew();
  };

  const continueLabel = isSavedSelected
    ? 'Continue with saved account'
    : isCmrSelected
      ? 'Continue with CMR details'
      : 'Continue with blank form';

  if (!open) return null;

  const hasSaved = existingBanks.length > 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bank-source-title"
    >
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-8 shadow-xl">
        <h2 id="bank-source-title" className="text-lg font-semibold text-gray-900">
          Choose saved or CMR bank details
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-gray-500">
          Pick one source for account number and IFSC. We will prefill the form below.
        </p>

        <div className="pt-5">
          <Radio.Group
            className="block w-full"
            value={selectedValue}
            onChange={(e) => setSelectedValue(e.target.value)}
          >
            <div className="flex w-full flex-col gap-4">
              {hasSaved ? (
                <div className="flex flex-col gap-4">
                  {existingBanks.length >= 2 && (
                    <p className="px-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
                      Saved on file
                    </p>
                  )}
                  {existingBanks.map((bank) => (
                    <OptionRow
                      key={bank.optionKey}
                      value={getBankOptionValue(bank)}
                      groupValue={selectedValue}
                      icon={FiDatabase}
                      iconClass="text-green-600"
                      title="Saved bank account"
                      subtitle={formatBankSummary(bank)}
                    />
                  ))}
                </div>
              ) : null}

              {hasCmrBank ? (
                <OptionRow
                  value="cmr"
                  groupValue={selectedValue}
                  icon={FiFileText}
                  iconClass="text-emerald-600"
                  title="From CMR document"
                  subtitle={
                    cmrMatchesSaved
                      ? 'Matches saved account · ' +
                        [cmrFields?.bank_name, cmrFields?.account_number, cmrFields?.ifsc]
                          .filter(Boolean)
                          .join(' · ')
                      : [cmrFields?.bank_name, cmrFields?.account_number, cmrFields?.ifsc]
                          .filter(Boolean)
                          .join(' · ') || 'Extracted from upload'
                  }
                />
              ) : null}

              <OptionRow
                value="new"
                groupValue={selectedValue}
                icon={FiPlus}
                iconClass="text-gray-500"
                title="Enter a new account manually"
                subtitle="No prefill — you type account number and IFSC."
              />
            </div>
          </Radio.Group>
        </div>

        <div className="mt-7">
          <button
            type="button"
            onClick={handleContinue}
            disabled={isSavedSelected && !selectedBank}
            className="w-full rounded-lg bg-green-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {continueLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
